import { Grid, Divider, Typography, Box, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Iconify from '../../../reusable-components/iconify';
import Markdown from '../../../reusable-components/markdown';
import Scrollbar from '../../../reusable-components/scrollbar';
import { fMonthDayYearShort } from '../../../utils/formatTime';
import { fToCurrency } from '../../../utils/formatText';
import { highlightText } from '../../../reusable-components/highlight-element/highlight-element';
import { useTabs } from '../../../context/TabContext';


InvoiceResult.propTypes = {
  searchResult: PropTypes.object,
  search: PropTypes.string,
  isDialog: PropTypes.bool,
  setIsDialogOpen: PropTypes.func
}

export default function InvoiceResult({searchResult, search, isDialog = false, setIsDialogOpen}) {
  const mediumAndUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const searchResultHasItems = useMemo(() => searchResult.glItems?.length > 0, [searchResult.glItems]);
  const [open, setOpen] = useState(searchResultHasItems);

  const handleClick = () => {
    setOpen(!open);
  };

  const { addTab } = useTabs();
  const navigate = useNavigate();

  const openInvoiceInNewTab = () => {
    addTab();
    navigate(`/dashboard/invoicing/${searchResult.sent ? 'previous' : 'pending'}/${searchResult.id}`);
  };

  const openOrderInNewTab = () => {
    addTab();
    navigate(`/dashboard/orders/${searchResult.orderId}`);
  };

  return <>
    <Grid
      container
      flexWrap="nowrap"
      justifyContent="space-between"
      alignItems="flex-start"
      sx={{ width: '100%' }}
    >
      <Grid item>
        <Box>
          <Typography variant="subtitle1" sx={{fontWeight: 'bold', display: 'flex'}} onClick={openInvoiceInNewTab}>
            Invoice #
            <Markdown
              sx={{fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer'}}
              inline
              convertPToDiv
              children={`${highlightText(searchResult.invoiceId, search)}`}
            />
          </Typography>
        </Box>
        <Typography variant="subtitle2">
          <Box display="flex" gap={1} flexWrap="wrap">
            {searchResult.invoiceId && (
              <Typography variant="subtitle2" onClick={openOrderInNewTab} sx={{cursor: 'pointer', display: 'flex'}}>
                Order #
                <Markdown sx={{textDecoration: 'underline'}} convertPToDiv children={`<s2>${highlightText(searchResult.orderNumber, search)}</s2>`} />
              </Typography>
            )}
          </Box>
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {searchResult.facility && searchResult.vendor ? (
            <Typography variant="subtitle2">
              <Markdown convertPToDiv children={`<s2>Facility: ${highlightText(searchResult.facility, search)} | Vendor: ${highlightText(searchResult.vendor, search)}</s2>`} />
            </Typography>
          ) : searchResult.facility ? <Typography variant="subtitle2">
              <Markdown convertPToDiv children={`<s2>Facility: ${highlightText(searchResult.facility, search)}</s2>`} />
            </Typography> : searchResult.vendor ?  <Typography variant="subtitle2">
              <Markdown convertPToDiv children={`<s2>Vendor: ${highlightText(searchResult.vendor, search)}</s2>`} />
            </Typography> : null
          }
        </Box>
        <Typography variant="subtitle2">
          <Markdown convertPToDiv children={`<s2>Invoice Total: ${highlightText(fToCurrency(searchResult.total), search)}</s2>`} />
        </Typography>
      </Grid>
      <Grid item sx={{display: 'flex', gap: '0.5rem'}}>
        <Typography variant="caption">{fMonthDayYearShort(searchResult.date)}</Typography>
        {!isDialog && <Iconify icon="mdi:open-in-new" color="primary" onClick={() => setIsDialogOpen(true)}/>}
        {searchResultHasItems &&
        <Iconify icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'}
          sx={{cursor: 'pointer'}}
          color="primary"
          width={16}
          height={16}
          onClick={handleClick}
        />}
      </Grid>
    </Grid>
    {open && (
      <Box sx={{ width: '100%' }}>
        <Divider sx={{ width: '100%', my: 1 }} />
        <Scrollbar sx={{maxHeight: isDialog ? '500px' : '225px', '& .simplebar-scrollbar:before': { opacity: 1 }}}>
          <Box sx={{display: "flex", gap: '0.4rem'}} flexWrap="wrap">
            {searchResult?.glItems?.map((item, idx) =>
              <Box key={idx} gap={1} sx={{
                backgroundColor: 'rgba(229, 233, 237, 0.42) ',
                borderRadius: '5px',
                padding: '15px',
                border: '1px solid #e5e9ed',
                boxSizing: 'border-box',
                flex: mediumAndUp ? '0 0 calc(50% - 0.2rem)' : '0 0 100%',
                display: "flex",
                alignItems: "center",
              }}>
                <Box>
                  <Markdown convertPToDiv children={`<s2>Gl Code: ${highlightText(item.glCode, search)}</s2>`} />
                  {item.glMemo && <Markdown convertPToDiv children={`<s2>Memo: ${highlightText(item.glMemo, search)}</s2>`} />}
                </Box>
              </Box>)}
          </Box>
        </Scrollbar>
      </Box>
    )}
  </>
}